import classNames from "classnames/bind";

import Button from "common/blocks/Button";
import Image from "common/blocks/Image";

import type { ResponseVehicle } from "services/motorpoint/shortlist";
import type { ResolvedShopfront } from "resolvers/data/shopfrontResolver";

import styles from "./FromView.module.scss";
import { DEFAULT_IMAGE_URL } from "lib/constants";
import formatCurrency from "utils/formatCurrency";
import SmartLink from "common/wrapper/SmartLink";
const cx = classNames.bind(styles);

type FromViewProps = ResolvedShopfront & {
    vehicleMakeModel: string;
    vehicles: ResponseVehicle[];
    shortlistedStocks?: number[];
};

const FromView = ({ vehicleMakeModel, vehicles, fromView }: FromViewProps) => {
    const cheapestVehicle = vehicles[0];
    const shopLink = [];

    const vehicleImageryOrdered = !fromView.overriddenImage
        ? []
        : cheapestVehicle?.FirstImage?.SizedImages?.sort((a, b) =>
              b.Format.localeCompare(a.Format)
          );

    if (cheapestVehicle) {
        shopLink.push(
            cheapestVehicle.IsCommercial ? "/used-vans" : "/used-cars",
            cheapestVehicle.Make.toLowerCase().replaceAll(" ", "-"),
            cheapestVehicle.Model.toLowerCase().replaceAll(" ", "-")
        );
    }

    return (
        <SmartLink href={shopLink.join("/")} target={fromView.fromCardTarget ?? ""}>
            <a className={cx("link")}>
                <div className={cx("card")}>
                    <div className={cx("image")}>
                        {vehicleImageryOrdered != null && vehicleImageryOrdered.length > 3 ? (
                            <picture className={cx("pictureWrapper")}>
                                {vehicleImageryOrdered.map((i) => {
                                    let mediaQueryString: string;
                                    switch (i.Size) {
                                        case "Medium":
                                            mediaQueryString = "(max-width: 743px)";
                                            break;
                                        case "Small":
                                            mediaQueryString = "(min-width: 744px)";
                                            break;
                                        default:
                                            return;
                                    }
                                    return (
                                        <source
                                            media={mediaQueryString}
                                            srcSet={i.Uri}
                                            type={`image/${i.Format}`}
                                        />
                                    );
                                })}
                                <img
                                    src={
                                        fromView.overriddenImage?.src ||
                                        cheapestVehicle.FirstImage?.Uri ||
                                        DEFAULT_IMAGE_URL
                                    }
                                    alt={
                                        fromView.overriddenImage?.alt ||
                                        cheapestVehicle.FirstImage.Description
                                    }
                                    className={cx("imageWrapper")}
                                    loading="eager"
                                />
                            </picture>
                        ) : (
                            <Image
                                {...fromView.overriddenImage}
                                layout="fill"
                                objectFit="cover"
                                src={
                                    fromView.overriddenImage?.src ||
                                    cheapestVehicle.FirstImage.Uri ||
                                    DEFAULT_IMAGE_URL
                                }
                                alt={
                                    fromView.overriddenImage?.alt ||
                                    cheapestVehicle.FirstImage.Description
                                }
                            />
                        )}
                    </div>
                    <div className={cx("body")}>
                        <div className={cx("cta")}>
                            <Button theme="textLink">
                                {fromView.ctaPrefixText} {vehicleMakeModel}
                            </Button>{" "}
                            from
                        </div>
                        <span className={cx("price")}>
                            <strong
                                dangerouslySetInnerHTML={{
                                    __html: formatCurrency(
                                        (cheapestVehicle?.CurrentPrice ?? 0) +
                                            (cheapestVehicle?.VatAmount ?? 0),
                                        0
                                    ),
                                }}
                            />
                            <span> / </span>
                            <strong
                                dangerouslySetInnerHTML={{
                                    __html: formatCurrency(
                                        cheapestVehicle?.LowestFinanceOption ?? 0,
                                        0
                                    ),
                                }}
                            />{" "}
                            month {cheapestVehicle?.LowestFinanceOptionType}
                        </span>
                    </div>
                </div>
            </a>
        </SmartLink>
    );
};

export default FromView;
